import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { FormInput, FormPhone, FormCheckbox } from '@podus/ui';
import { useSignUpMutation } from '@/features/api/authApi';
import { useAppDispatch } from '@/hooks/useStore';
import { setPendingVerification } from '@/features/auth/authSlice';
import { isAllowedRedirect, buildRedirectUrl } from '@/lib/redirect';
import { getAccessToken } from '@/lib/cookies';
import { PRODUCT_URLS } from '@podus/utils';

export default function SignUp() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const redirectParam = searchParams.get('redirect');

    // Auto-redirect if user is already logged in and has a redirect target
    useEffect(() => {
        const existingToken = getAccessToken();
        if (existingToken && redirectParam && isAllowedRedirect(redirectParam)) {
            window.location.href = buildRedirectUrl(redirectParam, existingToken);
        }
    }, [redirectParam]);

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [error, setError] = useState('');

    const [signUp, { isLoading }] = useSignUpMutation();

    const sanitizedPhone = useMemo(() => phone.replace(/\D/g, ''), [phone]);
    const trimmedEmail = useMemo(() => email.trim(), [email]);
    const trimmedFirstName = useMemo(() => firstName.trim(), [firstName]);
    const trimmedLastName = useMemo(() => lastName.trim(), [lastName]);

    const isSubmitDisabled =
        !trimmedFirstName ||
        !trimmedLastName ||
        !trimmedEmail ||
        !sanitizedPhone ||
        !password ||
        !acceptedTerms ||
        isLoading;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSubmitDisabled) return;
        setError('');

        try {
            await signUp({
                firstName: trimmedFirstName,
                lastName: trimmedLastName,
                email: trimmedEmail,
                phoneNumber: sanitizedPhone,
                password,
            }).unwrap();

            dispatch(
                setPendingVerification({
                    flow: 'signup',
                    email: trimmedEmail,
                    phoneNumber: sanitizedPhone,
                    ...(redirectParam && isAllowedRedirect(redirectParam) ? { redirectUrl: redirectParam } : {}),
                }),
            );

            navigate('/verify-otp');
        } catch (err: unknown) {
            const apiErr = err as {
                data?: { message?: string; response?: { message?: string } };
            };
            setError(apiErr?.data?.response?.message ?? apiErr?.data?.message ?? 'Sign up failed. Please try again.');
        }
    };

    return (
        <AuthLayout>
            <form onSubmit={handleSubmit} className="flex flex-col">
                <h1 className="text-2xl font-bold leading-tight tracking-[-0.5px] text-foreground sm:text-[32px] sm:leading-10">
                    Let&rsquo;s get you started
                </h1>

                <div className="mt-8 flex flex-col gap-3 sm:mt-12">
                    {/* Error message */}
                    {error && (
                        <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                            {error}
                        </div>
                    )}

                    <FormInput
                        id="signup-first-name"
                        name="firstName"
                        title="First Name"
                        placeholder="Success"
                        type="text"
                        required
                        variant="white"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                    />

                    <FormInput
                        id="signup-last-name"
                        name="lastName"
                        title="Last Name"
                        placeholder="Olatunde"
                        type="text"
                        required
                        variant="white"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                    />

                    <FormInput
                        id="signup-email"
                        name="email"
                        title="Email"
                        placeholder="johndoe@gmail.com"
                        type="email"
                        required
                        variant="white"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />

                    <FormPhone
                        id="signup-phone"
                        name="phone"
                        title="Phone Number"
                        value={phone}
                        onChange={setPhone}
                        required
                    />

                    <FormInput
                        id="signup-password"
                        name="password"
                        title="Password"
                        placeholder="••••••••••••••••••••"
                        type="password"
                        required
                        variant="white"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    {/* Terms & Privacy checkbox */}
                    <FormCheckbox
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        containerClassName="pt-1"
                        label={
                            <>
                                I agree to the{' '}
                                <a
                                    href={`${PRODUCT_URLS.podushelp}/terms`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-semibold text-primary hover:underline"
                                >
                                    Terms of Service
                                </a>{' '}
                                and{' '}
                                <a
                                    href={`${PRODUCT_URLS.podushelp}/privacy`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-semibold text-primary hover:underline"
                                >
                                    Privacy Policy
                                </a>
                            </>
                        }
                    />
                </div>

                {/* Sign Up Button */}
                <button
                    type="submit"
                    disabled={isSubmitDisabled}
                    className="mt-6 h-11 w-full cursor-pointer rounded-[10px] bg-btn text-sm font-bold leading-6 text-btn-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 sm:mt-7 sm:h-12"
                >
                    {isLoading ? 'Creating account...' : 'Sign Up'}
                </button>

                <p className="mt-4 text-center text-sm leading-6 text-foreground">
                    Have an account?{' '}
                    <Link
                        to={redirectParam ? `/sign-in?redirect=${encodeURIComponent(redirectParam)}` : '/sign-in'}
                        className="font-bold text-primary"
                    >
                        Log in
                    </Link>
                </p>
            </form>
        </AuthLayout>
    );
}
